import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Loader2, RefreshCw, UploadCloud, Activity } from "lucide-react";
import api, { fmtINR, formatDetail, fmtNum } from "@/lib/api";
import Modal from "@/components/Modal";
import DepositCountPicker, { fmtCategories } from "@/components/DepositCountPicker";

const DEFAULT_LIMITS = {
    min_amount: 100, max_amount: 10000, daily_limit: 100000, monthly_limit: 2000000,
    max_daily_txn: 80, max_monthly_txn: 2000, active_from_hour: 0, active_to_hour: 23,
    allowed_deposit_counts: [0, 1, 2, 3, 4], min_deposit_count: null,
    active: true, cooldown_seconds: 0,
};

const STATUS_PILL = {
    available: { cls: "pill-success", label: "live" },
    cooling: { cls: "pill-pending", label: "cooling" },
    limit_blocked: { cls: "pill-failed", label: "limit hit" },
    off_hours: { cls: "pill-muted", label: "off-hours" },
    disabled: { cls: "pill-failed", label: "disabled" },
};

export default function ChannelsPage({ kind = "upi" }) {
    const isUpi = kind === "upi";
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [healthOpen, setHealthOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    // Per-channel health snapshot (24h decline rates). Refreshed alongside
    // the channel list so a hot handle gets a visible pill on its card.
    const [health, setHealth] = useState({});
    const url = isUpi ? "/partner/upi-channels" : "/partner/bank-accounts";
    const load = async () => {
        const r = await api.get(url); setItems(r.data.items);
        if (isUpi) {
            try {
                const h = await api.get("/partner/upi-channels/health");
                setHealth(h.data.channels || {});
            } catch (e) { console.warn("UPI channel health fetch failed:", e); /* health endpoint optional */ }
        }
    };
    useEffect(() => { load();   }, [kind]);
    // Auto-refresh card states every 5s so cooldown countdowns + limit
    // resets stay live without a manual page reload.
    useEffect(() => {
        const id = setInterval(load, 5000);
        return () => clearInterval(id);
         
    }, [kind]);
    const remove = async (cid) => {
        if (!window.confirm("Delete this channel?")) return;
        await api.delete(`${url}/${cid}`); load();
    };
    const rotate = async (cid) => {
        await api.post(`${url}/${cid}/rotate`);
        load();
    };
    return (
        <div className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">{isUpi ? "UPI Channels" : "Bank Accounts"}</h1>
                    <p className="text-sm text-slate-500 mt-1">{isUpi ? "Configure UPI IDs / QR codes with limits." : "Bank accounts for direct transfer."}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isUpi && (
                        <>
                            <button data-testid="upi-health-btn" onClick={() => setHealthOpen(true)}
                                    className="btn-secondary px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
                                <Activity className="w-4 h-4" /> Health monitor
                            </button>
                            <button data-testid="upi-bulk-btn" onClick={() => setBulkOpen(true)}
                                    className="btn-secondary px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
                                <UploadCloud className="w-4 h-4" /> Bulk import
                            </button>
                        </>
                    )}
                    <button data-testid={`add-${kind}-btn`} onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Add {isUpi ? "UPI" : "Bank"}
                    </button>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.length === 0 && <div className="col-span-full card p-12 text-center text-slate-400 text-sm">No {isUpi ? "UPI channels" : "bank accounts"} yet.</div>}
                {items.map((c) => (
                    <ChannelCard key={c.channel_id} c={c} isUpi={isUpi}
                                 health={isUpi ? health[c.channel_id] : null}
                                 onEdit={() => { setEditing(c); setOpen(true); }}
                                 onDelete={() => remove(c.channel_id)}
                                 onRotate={() => rotate(c.channel_id)} />
                ))}
            </div>

            {open && <ChannelModal kind={kind} editing={editing} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
            {bulkOpen && <BulkImportModal onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
            {healthOpen && <HealthSettingsModal onClose={() => setHealthOpen(false)} />}
        </div>
    );
}

function ChannelCard({ c, isUpi, health, onEdit, onDelete, onRotate }) {
    // Live cooldown countdown: tick every 1s locally so the user sees
    // "42s · 41s · 40s …" without a server poll. Re-syncs on every poll
    // (parent refetches every 5s) via the c.cooldown_remaining_seconds prop.
    const [cdLeft, setCdLeft] = useState(c.cooldown_remaining_seconds || 0);
    useEffect(() => { setCdLeft(c.cooldown_remaining_seconds || 0); }, [c.cooldown_remaining_seconds, c.last_used_at]);
    useEffect(() => {
        if (cdLeft <= 0) return;
        const id = setInterval(() => setCdLeft((v) => Math.max(0, v - 1)), 1000);
        return () => clearInterval(id);
    }, [cdLeft]);

    const liveStatus = cdLeft > 0 && c.active && c.in_active_window
        ? "cooling"
        : (c.availability_status || (c.active ? (c.in_active_window ? "available" : "off_hours") : "disabled"));
    const pill = STATUS_PILL[liveStatus] || STATUS_PILL.available;
    const pillLabel = liveStatus === "cooling" ? `cooling · ${cdLeft}s` : pill.label;
    const cdConfigured = (c.cooldown_seconds || 0) > 0;

    return (
        <div data-testid={`channel-${c.channel_id}`} className="card p-5">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="font-semibold truncate">{c.label}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">{isUpi ? c.upi_id : `${c.bank_name} · ${c.account_number}`}</div>
                </div>
                <span data-testid={`channel-status-${c.channel_id}`}
                      className={`pill ${pill.cls} flex-shrink-0`}>
                    {pillLabel}
                </span>
            </div>
            {/* Auto-disable banner — shows both the historical health-monitor
                field (`auto_disable_reason`) AND the new failure-threshold
                field (`auto_disabled_reason`, with the trailing 'ed'). Two
                fields on purpose so the two subsystems can co-exist without
                stepping on each other's state. */}
            {isUpi && c.auto_disabled_at && (
                <div data-testid={`channel-auto-disabled-${c.channel_id}`}
                     className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-900 leading-snug">
                    <div className="font-semibold">Auto-disabled</div>
                    <div className="opacity-80">{c.auto_disabled_reason || c.auto_disable_reason || "decline rate above threshold"}</div>
                    {c.consecutive_fail_count > 0 && (
                        <div className="mt-0.5 opacity-70">Consecutive fails: {c.consecutive_fail_count}</div>
                    )}
                </div>
            )}
            {/* 24h health pill — always shown when we have data; volume
                threshold lives server-side. */}
            {isUpi && health && health.total > 0 && (
                <div data-testid={`channel-health-${c.channel_id}`}
                     className={`mt-2 inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                       health.decline_rate > 0.4 ? "bg-rose-100 text-rose-800"
                       : health.decline_rate > 0.2 ? "bg-amber-100 text-amber-800"
                       : "bg-emerald-100 text-emerald-800"
                     }`}>
                    <Activity className="w-3 h-3" />
                    {Math.round(health.decline_rate * 100)}% declines · {health.success}✓ / {health.fail}✗ · 24h
                </div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                <Field label="Per txn" value={`${fmtINR(c.min_amount)} – ${fmtINR(c.max_amount)}`} />
                <Field label="Active hrs" value={`${String(c.active_from_hour).padStart(2,'0')}:00 – ${String(c.active_to_hour).padStart(2,'0')}:00 IST`} />
                <Field label="Daily" value={`${fmtINR(c.used_amount_today)} / ${fmtINR(c.daily_limit)}`} />
                <Field label="Monthly" value={`${fmtINR(c.used_amount_month || 0)} / ${fmtINR(c.monthly_limit)}`} />
                <Field label="Daily txns" value={`${fmtNum(c.txn_count_today)} / ${fmtNum(c.max_daily_txn)}`} />
                <Field label="Categories" value={fmtCategories(c.allowed_deposit_counts, c.min_deposit_count)} />
                <Field label="Cooldown" value={cdConfigured ? `${c.cooldown_seconds}s rest` : "off"} />
                <div />
            </div>
            <div className="mt-4 flex justify-end gap-2 flex-wrap">
                {cdConfigured && (
                    <button data-testid={`rotate-${c.channel_id}`}
                            onClick={onRotate}
                            disabled={cdLeft > 0}
                            title={cdLeft > 0 ? `Resting · ${cdLeft}s left` : `Rest this channel for ${c.cooldown_seconds}s`}
                            className="btn-secondary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
                        <RefreshCw className={`w-3.5 h-3.5 ${cdLeft > 0 ? "animate-spin" : ""}`} /> Rotate now
                    </button>
                )}
                <button onClick={onEdit} className="btn-secondary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={onDelete} className="btn-danger px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
        </div>
    );
}

function Field({ label, value }) {
    return <div><div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div><div className="font-mono mt-0.5">{value}</div></div>;
}

function ChannelModal({ kind, editing, onClose, onDone }) {
    const isUpi = kind === "upi";
    const baseFields = isUpi
        ? { label: "", upi_id: "", payee_name: "", qr_image: "" }
        : { label: "", account_holder: "", account_number: "", ifsc: "", bank_name: "" };
    const [f, setF] = useState({ ...baseFields, ...DEFAULT_LIMITS, ...(editing || {}) });
    const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
    const url = isUpi ? "/partner/upi-channels" : "/partner/bank-accounts";

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            const payload = { ...f };
            ["min_amount","max_amount","daily_limit","monthly_limit","max_daily_txn","max_monthly_txn","active_from_hour","active_to_hour","cooldown_seconds"].forEach((k) => {
                payload[k] = Number(payload[k] || 0);
            });
            payload.allowed_deposit_counts = (payload.allowed_deposit_counts || []).map(Number);
            payload.min_deposit_count = payload.min_deposit_count == null || payload.min_deposit_count === ""
                ? null : Number(payload.min_deposit_count);
            if (editing) await api.patch(`${url}/${editing.channel_id}`, payload);
            else await api.post(url, payload);
            onDone();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <Modal testId={`${kind}-modal`} onClose={onClose} title={`${editing ? "Edit" : "Add"} ${isUpi ? "UPI channel" : "bank account"}`} size="lg">
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="label">Label</label>
                    <input data-testid="ch-label" required className="input" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder={isUpi ? "UPI Alpha" : "Bank A"} /></div>

                {isUpi ? (
                    <>
                        <div><label className="label">UPI ID</label><input data-testid="ch-upi" required className="input" value={f.upi_id} onChange={(e) => setF({ ...f, upi_id: e.target.value })} placeholder="yourbusiness@oksbi" /></div>
                        <div><label className="label">Payee name</label><input data-testid="ch-payee" required className="input" value={f.payee_name} onChange={(e) => setF({ ...f, payee_name: e.target.value })} /></div>
                        <div className="col-span-2"><label className="label">QR image (paste base64 data url, optional)</label>
                            <textarea data-testid="ch-qr" rows={2} className="input font-mono text-xs" value={f.qr_image || ""} onChange={(e) => setF({ ...f, qr_image: e.target.value })} placeholder="data:image/png;base64,..." /></div>
                    </>
                ) : (
                    <>
                        <div><label className="label">Bank name</label><input data-testid="ch-bank" required className="input" value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} /></div>
                        <div><label className="label">Account holder</label><input data-testid="ch-holder" required className="input" value={f.account_holder} onChange={(e) => setF({ ...f, account_holder: e.target.value })} /></div>
                        <div><label className="label">Account number</label><input data-testid="ch-acno" required className="input font-mono" value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value })} /></div>
                        <div><label className="label">IFSC</label><input data-testid="ch-ifsc" required className="input font-mono uppercase" value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} /></div>
                    </>
                )}

                <div className="col-span-2 mt-2 pt-3 border-t border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">Limits & counters</div>

                <div><label className="label">Min amount (₹)</label><input data-testid="ch-min" required type="number" className="input font-mono" value={f.min_amount} onChange={(e) => setF({ ...f, min_amount: e.target.value })} /></div>
                <div><label className="label">Max amount (₹)</label><input data-testid="ch-max" required type="number" className="input font-mono" value={f.max_amount} onChange={(e) => setF({ ...f, max_amount: e.target.value })} /></div>
                <div><label className="label">Daily volume limit (₹)</label><input data-testid="ch-daily" required type="number" className="input font-mono" value={f.daily_limit} onChange={(e) => setF({ ...f, daily_limit: e.target.value })} /></div>
                <div><label className="label">Monthly volume limit (₹)</label><input data-testid="ch-monthly" required type="number" className="input font-mono" value={f.monthly_limit} onChange={(e) => setF({ ...f, monthly_limit: e.target.value })} /></div>
                <div><label className="label">Max daily txns</label><input data-testid="ch-dtxn" required type="number" className="input font-mono" value={f.max_daily_txn} onChange={(e) => setF({ ...f, max_daily_txn: e.target.value })} /></div>
                <div><label className="label">Max monthly txns</label><input data-testid="ch-mtxn" required type="number" className="input font-mono" value={f.max_monthly_txn} onChange={(e) => setF({ ...f, max_monthly_txn: e.target.value })} /></div>

                <div><label className="label">Active from (hour IST)</label><input data-testid="ch-fhr" required type="number" min="0" max="23" className="input font-mono" value={f.active_from_hour} onChange={(e) => setF({ ...f, active_from_hour: e.target.value })} /></div>
                <div><label className="label">Active to (hour IST)</label><input data-testid="ch-thr" required type="number" min="0" max="23" className="input font-mono" value={f.active_to_hour} onChange={(e) => setF({ ...f, active_to_hour: e.target.value })} /></div>

                <div className="col-span-2">
                    <label className="label">
                        Cooldown <span className="text-slate-400 text-[10px]">· seconds the channel rests after each pick · 0 disables auto-rotate</span>
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                        <input data-testid="ch-cooldown" type="number" min="0" max="3600" className="input font-mono w-32"
                               value={f.cooldown_seconds ?? 0}
                               onChange={(e) => setF({ ...f, cooldown_seconds: e.target.value })} />
                        <div className="flex gap-1.5 flex-wrap">
                            {[0, 30, 60, 120, 300].map((n) => (
                                <button key={n} type="button"
                                        data-testid={`ch-cd-preset-${n}`}
                                        onClick={() => setF({ ...f, cooldown_seconds: n })}
                                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition ${
                                            Number(f.cooldown_seconds) === n
                                                ? "bg-blue-50 border-blue-200 text-blue-700"
                                                : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                                        }`}>
                                    {n === 0 ? "off" : `${n}s`}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-span-2">
                    <label className="label">Allowed deposit-count categories</label>
                    <DepositCountPicker
                        testId="ch-cat"
                        value={f.allowed_deposit_counts}
                        onChange={(next) => setF({ ...f, allowed_deposit_counts: next })}
                        threshold={f.min_deposit_count ?? null}
                        onThresholdChange={(n) => setF({ ...f, min_deposit_count: n })}
                    />
                </div>

                <div className="col-span-2 flex items-center gap-2"><input id="active" type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /><label htmlFor="active" className="text-sm">Active</label></div>

                {err && <div className="col-span-2 text-sm text-rose-600">{err}</div>}
                <div className="col-span-2 flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md text-sm">Cancel</button>
                    <button data-testid="ch-submit" type="submit" disabled={busy} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save" : "Create"}</button>
                </div>
            </form>
        </Modal>
    );
}


function BulkImportModal({ onClose, onDone }) {
    // Accepts pasted lines in 1-4 column CSV form:
    //   upi_id [, label [, min_amount, max_amount [, daily_limit]]]
    // — only `upi_id` is mandatory; the rest fall back to safe defaults.
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");

    const parseRows = () => {
        const out = [];
        for (const raw of text.split(/\r?\n/)) {
            const line = raw.trim();
            if (!line || line.startsWith("#")) continue;
            const parts = line.split(",").map((p) => p.trim());
            const [upi_id, label, min_amount, max_amount, daily_limit] = parts;
            if (!upi_id) continue;
            const r = { upi_id };
            if (label) r.label = label;
            if (min_amount && !Number.isNaN(parseFloat(min_amount))) r.min_amount = parseFloat(min_amount);
            if (max_amount && !Number.isNaN(parseFloat(max_amount))) r.max_amount = parseFloat(max_amount);
            if (daily_limit && !Number.isNaN(parseFloat(daily_limit))) r.daily_limit = parseFloat(daily_limit);
            out.push(r);
        }
        return out;
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setResult(null);
        const rows = parseRows();
        if (rows.length === 0) { setErr("Please paste at least one UPI handle."); return; }
        setBusy(true);
        try {
            const r = await api.post("/partner/upi-channels/bulk-import", { rows, skip_duplicates: true });
            setResult(r.data);
        } catch (e2) {
            setErr(formatDetail(e2?.response?.data?.detail) || "Bulk import failed");
        } finally { setBusy(false); }
    };

    return (
        <Modal testId="bulk-import-modal" onClose={onClose} title="Bulk-import UPI handles">
            <form onSubmit={submit} className="space-y-3">
                <div className="text-xs text-slate-600 leading-relaxed bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    Paste one UPI handle per line. Optional columns (comma-separated):<br />
                    <code className="font-mono text-[11px]">upi_id, label, min_amount, max_amount, daily_limit</code><br />
                    Lines starting with <code>#</code> are ignored. Duplicates skipped automatically.
                </div>
                <textarea
                    data-testid="bulk-textarea"
                    className="input min-h-[150px] font-mono text-[11px]"
                    placeholder="acme.alpha@oksbi, UPI Alpha, 100, 50000, 500000&#10;acme.beta@okhdfcbank&#10;# this line is a comment&#10;acme.gamma@upi"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                />
                {err && <div className="text-xs text-rose-600">{err}</div>}
                {result && (
                    <div data-testid="bulk-result" className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 max-h-40 overflow-auto">
                        <div className="font-semibold mb-1">
                            <span className="text-emerald-700">{result.created} created</span> · <span className="text-amber-700">{result.skipped} skipped</span>
                        </div>
                        <ul className="space-y-0.5 font-mono text-[10px]">
                            {(result.results || []).map((r, i) => (
                                <li key={r.upi_id ? `${r.upi_id}-${i}` : `row-${i}`} className={r.ok ? "text-emerald-700" : "text-rose-700"}>
                                    {r.ok ? "✓" : "✗"} {r.upi_id || "(empty)"} {r.reason ? `— ${r.reason}` : ""}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={result ? onDone : onClose} className="btn-secondary px-4 py-2 rounded-md text-sm">
                        {result ? "Close" : "Cancel"}
                    </button>
                    {!result && (
                        <button data-testid="bulk-submit" type="submit" disabled={busy}
                                className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Import
                        </button>
                    )}
                </div>
            </form>
        </Modal>
    );
}


function HealthSettingsModal({ onClose }) {
    // Edits the partner's `health_monitor_enabled` + `health_threshold` +
    // `health_cool_hours` settings via PATCH /partner/profile. We hydrate
    // from /api/auth/me on mount so the modal opens with current values.
    const [enabled, setEnabled] = useState(false);
    const [threshold, setThreshold] = useState(40);  // shown as %, sent as 0-1
    const [coolHours, setCoolHours] = useState(6);
    const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/auth/me");
                const p = r.data?.partner || {};
                setEnabled(!!p.health_monitor_enabled);
                if (p.health_threshold) setThreshold(Math.round(p.health_threshold * 100));
                if (p.health_cool_hours) setCoolHours(p.health_cool_hours);
            } catch (e) { console.warn("partner health settings fetch failed:", e); /* fallback to defaults */ }
        })();
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setErr(""); setDone(false); setBusy(true);
        try {
            await api.patch("/partner/profile", {
                health_monitor_enabled: enabled,
                health_threshold: threshold / 100,
                health_cool_hours: coolHours,
            });
            setDone(true);
            setTimeout(() => onClose(), 800);
        } catch (e2) {
            setErr(formatDetail(e2?.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    };

    return (
        <Modal testId="health-modal" onClose={onClose} title="UPI handle health monitor">
            <form onSubmit={save} className="space-y-4">
                <div className="text-xs text-slate-600 leading-relaxed bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                    Auto-disables UPI handles whose 24h decline rate exceeds the threshold for the cool-down window. Stops one bad handle from poisoning the rest of your channel pool. Re-enables automatically once the cool-down elapses. Manual disables are never auto-revived.
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                    <input data-testid="health-enabled" type="checkbox" className="mt-0.5"
                           checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                    <div>
                        <div className="text-sm font-medium text-slate-800">Enable health monitor</div>
                        <div className="text-[11px] text-slate-500">When OFF, your handles are never auto-disabled.</div>
                    </div>
                </label>
                <div className={`grid grid-cols-2 gap-3 ${enabled ? "" : "opacity-50 pointer-events-none"}`}>
                    <div>
                        <label className="label">Decline-rate threshold</label>
                        <div className="flex items-center gap-2">
                            <input data-testid="health-threshold" type="number" min="5" max="95" step="1"
                                   value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value || "0", 10))}
                                   className="input !w-20" />
                            <span className="text-xs text-slate-500">% over 24h</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Default 40%. Lower = more aggressive.</div>
                    </div>
                    <div>
                        <label className="label">Cool-down period</label>
                        <div className="flex items-center gap-2">
                            <input data-testid="health-cool" type="number" min="1" max="72" step="1"
                                   value={coolHours} onChange={(e) => setCoolHours(parseInt(e.target.value || "0", 10))}
                                   className="input !w-20" />
                            <span className="text-xs text-slate-500">hours</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Default 6h.</div>
                    </div>
                </div>
                {err && <div className="text-xs text-rose-600">{err}</div>}
                {done && <div className="text-xs text-emerald-700">Saved.</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md text-sm">Cancel</button>
                    <button data-testid="health-save" type="submit" disabled={busy}
                            className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
                    </button>
                </div>
            </form>
        </Modal>
    );
}
